/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useEffect, useReducer, useMemo, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { Link } from 'react-router-dom';

import connectData, { ConnectedProps } from '../../support/connectData';
import SaleActions from '../../support/SaleActions';
import getVendorCategories from '../../support/helpers/getVendorCategories';
import { canUser } from '../../support/shared/permissionHelpers';

import {
  DEFAULT_MARKET_NOTE_CATEGORIES,
  DEFAULT_MARKET_PAYMENTS,
} from '../../support/shared/constants/Constants';
import formatDate from '../../core/support/formatDate';
import IndexedList from '../../support/shared/IndexedList';

import LoadingIndicator from '../uiold/LoadingIndicator';
import {
  Button,
  ButtonToolbar,
  IconButton,
  Page,
  Picker,
  Text,
  TextInputSearch,
} from '../uiold';
import { themeColors } from '../uiold/Constants';

import { MarketSalesProvider } from './Context';
import MarketSalesTable from './MarketSalesTable';
import ManagePayments from './ManagePayments';
import ExportSalesDialog from './ExportSalesDialog';

import { Market } from '../../types/shared/Market';
import {
  VendorNote,
  VendorSale,
  PaymentsAndSalesByMarket,
} from '../../types/shared/MarketSales';
import { fixMarketNotes } from '../../support/fixMarketNotes';

type Action =
  | { type: 'MARKET_SELECT'; market: Market }
  | { type: 'DATE_SELECT'; date: string }
  | { type: 'CATEGORY_SELECT'; category: string }
  | { type: 'SHOW_MANAGE_PAYMENTS' }
  | { type: 'HIDE_MANAGE_PAYMENTS' }
  | { type: 'SEARCH_KEYWORD_CHANGE'; searchKeyword: string }
  | { type: 'TOGGLE_EXPORT_DIALOG' };

type State = {
  activeDate: string | null | undefined;
  activeCategory: string | null | undefined;
  activeMarket: Market | null | undefined;
  isManagePaymentsVisible: boolean;
  isExportDialogVisible: boolean;
  searchKeyword: string;
};

let ALL_CATEGORY = t('All Categories');

let initialState: State = {
  activeCategory: ALL_CATEGORY,
  activeDate: null,
  activeMarket: null,
  isManagePaymentsVisible: false,
  isExportDialogVisible: false,
  searchKeyword: '',
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'MARKET_SELECT': {
      return {
        ...state,
        activeMarket: action.market,
        activeDate: action.market.dates[0],
      };
    }
    case 'DATE_SELECT': {
      return { ...state, activeDate: action.date };
    }
    case 'CATEGORY_SELECT': {
      return { ...state, activeCategory: action.category };
    }
    case 'SHOW_MANAGE_PAYMENTS': {
      return { ...state, isManagePaymentsVisible: true };
    }
    case 'HIDE_MANAGE_PAYMENTS': {
      return { ...state, isManagePaymentsVisible: false };
    }
    case 'TOGGLE_EXPORT_DIALOG': {
      return { ...state, isExportDialogVisible: !state.isExportDialogVisible };
    }
    case 'SEARCH_KEYWORD_CHANGE': {
      return { ...state, searchKeyword: action.searchKeyword };
    }
    default:
      return state;
  }
}

function Redemptions(props: FullProps) {
  let {
    activeVendors,
    filteredMarkets,
    formDef,
    placement,
    positionMap,
    sales: allSales,
    isLoading,
    isDirty,
    isSaving,
    fetchAll,
    user,
    saveSales,
  } = props;

  let [
    {
      activeMarket,
      activeDate,
      activeCategory,
      isManagePaymentsVisible,
      searchKeyword,
      isExportDialogVisible,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  let fetchedSales = useRef<PaymentsAndSalesByMarket>({});
  useMemo(() => {
    if (!isDirty && allSales) {
      fetchedSales.current = allSales;
    }
  }, [isDirty, allSales]);

  useEffect(() => {
    if (isLoading || !filteredMarkets) {
      return;
    }
    let market = filteredMarkets.getByIndex(0);
    if (!market) {
      return;
    }
    let [date] = market.dates;
    if (!date) {
      return;
    }
    dispatch({ type: 'MARKET_SELECT', market });
    dispatch({ type: 'DATE_SELECT', date });
  }, [isLoading, filteredMarkets]);

  let categories = useMemo(
    () => [
      ALL_CATEGORY,
      ...((formDef && getVendorCategories(formDef as any)) || []),
    ],
    [formDef],
  );

  useEffect(() => {
    let [category] = categories;
    if (!category) {
      return;
    }
    dispatch({ type: 'CATEGORY_SELECT', category });
  }, [categories]);

  let activeMarketDates = useMemo(
    () =>
      (activeMarket &&
        activeMarket.dates.map((date) => {
          let marketDate = {
            date,
            name: formatDate(date, { fromUTC: true, includeDayOfWeek: true }),
          };
          return marketDate;
        })) ||
      [],
    [activeMarket],
  );

  let marketSalesContext = useMemo(
    () => ({
      activeMarket,
      activeDate,
      allSales,
      onPlacementCellBlur: (params: {
        vendorID: string;
        paymentID?: string;
        noteCategoryID?: string;
        value: VendorSale | VendorNote;
      }) => {
        const { vendorID, paymentID, noteCategoryID, value } = params;
        if (!activeMarket || !activeDate || (!noteCategoryID && !paymentID)) {
          return;
        }

        SaleActions.updateVendorSale({
          marketID: activeMarket.id,
          date: activeDate,
          vendorID,
          categoryID: noteCategoryID || paymentID || '',
          sale: paymentID && typeof value === 'number' ? value : null,
          note: noteCategoryID && typeof value === 'string' ? value : null,
        });
      },
    }),
    [activeMarket, activeDate, allSales],
  );

  if (
    isLoading ||
    !activeVendors ||
    !filteredMarkets ||
    !placement ||
    !positionMap ||
    !allSales
  ) {
    return <Page isLoading={true} />;
  }

  if (!activeMarket || !activeDate) {
    return (
      <Page>
        <Page.Header title="Redemptions" />
        <View style={styles.root}>
          <Text type="label">
            {`You don't have any market dates. `}
            <Link to="/markets">Click Here</Link> to edit your markets.
          </Text>
        </View>
      </Page>
    );
  }

  let positionByActiveMarket =
    (positionMap && positionMap[activeMarket.id]) || {};

  const marketSales = allSales[activeMarket.id];
  const { sales, payments, noteCategories } = marketSales ?? {};
  const notes = fixMarketNotes(marketSales?.notes);

  let marketPayments = {
    ...DEFAULT_MARKET_PAYMENTS,
    ...payments,
  };

  let marketNoteCategories = {
    ...DEFAULT_MARKET_NOTE_CATEGORIES,
    ...noteCategories,
  };

  let marketSalesByVendor = sales?.[activeDate] || {};
  let marketNotesByVendor = notes?.[activeDate] || {};

  let keyword = searchKeyword.toLowerCase();
  let vendors = activeVendors
    .toArray()
    .flatMap((vendor) => {
      let vendorPlacementIndexes =
        placement?.[vendor.id]?.[activeMarket?.id ?? '']?.[activeDate ?? ''] ||
        [];

      let isVendorPlaced = !!vendorPlacementIndexes.length;

      // Don't include vendor that is not placed.
      if (!isVendorPlaced) {
        return [];
      }

      let vendorPlacement = vendorPlacementIndexes.map((placementIndex) => {
        let stallName = positionByActiveMarket[String(placementIndex)];
        // Display stall name if exist, otherwise, just display a number.
        return stallName || String(placementIndex + 1);
      });

      return {
        ...vendor,
        placement: vendorPlacement,
        sales: marketSalesByVendor[vendor.id] || {},
        marketNotes: marketNotesByVendor[vendor.id] || {},
      };
    }) // Filter the vendors by selected category.
    .filter((vendor) => {
      if (activeCategory === ALL_CATEGORY) {
        return true;
      }
      if (!vendor.category) {
        return false;
      }
      return vendor.category.includes(activeCategory ?? '');
    })
    .filter((vendor) =>
      [
        // Will only search for match in this array
        vendor.name,
        vendor.contact,
        (vendor.category || []).toString(),
      ].some((w) =>
        typeof w === 'string' ? w.toLowerCase().includes(keyword) : false,
      ),
    )
    .filter((vendor) => {
      return activeMarket && activeDate
        ? vendor.markets[activeMarket.id]?.includes(activeDate)
        : false;
    });

  return (
    <Page>
      <Page.Header title="Redemptions">
        <View style={styles.save}>
          {isSaving && <LoadingIndicator />}
          {!isSaving && isDirty && (
            <Fragment>
              <Button onPress={saveSales} title="Save" isPrimary />
              <Button onPress={fetchAll} title="Revert" />
            </Fragment>
          )}
        </View>
      </Page.Header>
      <ButtonToolbar style={styles.toolbar}>
        <Picker
          options={filteredMarkets.getList()}
          selectedItem={activeMarket}
          onChange={(market) => dispatch({ type: 'MARKET_SELECT', market })}
          style={{ marginRight: 5 }}
        />
        <Picker
          options={activeMarketDates}
          isSelected={(item) => item.date === activeDate}
          onChange={({ date }) => dispatch({ type: 'DATE_SELECT', date })}
          style={{ marginRight: 5 }}
        />
        <Picker
          options={categories}
          selectedItem={activeCategory ?? undefined}
          onChange={(category) =>
            dispatch({ type: 'CATEGORY_SELECT', category })
          }
          style={{ marginRight: 5 }}
        />
        <IconButton
          tooltip="Export"
          iconName="export"
          onPress={() => dispatch({ type: 'TOGGLE_EXPORT_DIALOG' })}
          style={{ marginRight: 5 }}
        />
        <View style={styles.toolbarSpacing} />
        {user && canUser(user, { marketSales: 'WRITE' }) && (
          <IconButton
            tooltip="Manage Payment Methods"
            iconName="settings"
            onPress={() => dispatch({ type: 'SHOW_MANAGE_PAYMENTS' })}
            style={{ marginRight: 5 }}
          />
        )}

        <TextInputSearch
          value={searchKeyword}
          onChangeText={(searchKeyword) =>
            dispatch({ type: 'SEARCH_KEYWORD_CHANGE', searchKeyword })
          }
          containerStyle={styles.searchInput}
        />
      </ButtonToolbar>
      <MarketSalesProvider value={marketSalesContext}>
        <MarketSalesTable
          vendors={vendors}
          marketPayments={marketPayments}
          marketNoteCategories={marketNoteCategories}
          user={user}
        />
        {isManagePaymentsVisible && (
          <ManagePayments
            onClose={() => dispatch({ type: 'HIDE_MANAGE_PAYMENTS' })}
            marketPayments={marketPayments}
          />
        )}
      </MarketSalesProvider>
      {isExportDialogVisible ? (
        <ExportSalesDialog
          account={props.account}
          season={props.season}
          markets={filteredMarkets.getList()}
          onDismiss={() => dispatch({ type: 'TOGGLE_EXPORT_DIALOG' })}
        />
      ) : null}
    </Page>
  );
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
    padding: 20,
    backgroundColor: themeColors.backgroundGrey,
  },
  toolbar: {
    zIndex: 1,
  },
  toolbarSpacing: {
    flex: 1,
  },
  searchInput: {
    maxWidth: 250,
  },
  save: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
});

const connector = connectData()(
  () => ({
    account: { dataStoreKey: 'account' },
    user: { dataStoreKey: 'user' },
    season: { dataStoreKey: 'season' },
    activeVendors: { dataStoreKey: 'vendors/active' },
    formDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
    markets: { dataStoreKey: 'markets' },
    placement: { dataStoreKey: 'placement' },
    positionMap: { dataStoreKey: 'position' },
    sales: { dataStoreKey: 'sales' },
  }),
  () => ({
    saveSales: { dataStoreKey: 'sales' },
  }),
  {
    computedProps: {
      filteredMarkets: {
        props: ['markets'],
        compute: (markets: IndexedList<Market> | null | undefined) => {
          if (!markets) {
            return null;
          }
          return markets.filter(
            (market) => market.dates != null && market.dates.length !== 0,
          );
        },
      },
    },
  },
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(Redemptions);
